import { useState } from "react";

import { useProfitLossCharts } from "../api/hooks/useProfitLossCharts";
import { useWinRateCharts } from "../api/hooks/useWinRateCharts";
import type { components } from "../api/schema";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useGlobalFilters } from "../lib/globalFilters";
import { formatMoney } from "../lib/money";
import { cn } from "../lib/cn";

// The merged "Charts" tab (Analytics restructure Unit 2): one surface fronting
// both the P&L per-bucket views and the win-rate per-bucket views, switched by
// a metric toggle. The toggle swaps the whole view -- data source, dimension
// tabs, bar rendering, and the Auto-Insights side panel -- because the two
// metrics come from different endpoints with mostly distinct dimensions. Only
// the active metric's query runs. (Route stays /reports/profit-loss-charts; the
// "Charts" label living there is an intentional, accepted URL/label mismatch.)

type ProfitLossChartsResponse = components["schemas"]["ProfitLossChartsResponse"];
type ProfitLossView = ProfitLossChartsResponse["by_month"];
type ProfitLossBucket = ProfitLossView["buckets"][number];
type ProfitLossInsights = ProfitLossView["auto_insights"];

type WinRateChartsResponse = components["schemas"]["WinRateChartsResponse"];
type WinRateView = WinRateChartsResponse["by_price_range"];
type WinRateBucket = WinRateView["buckets"][number];
type WinRateInsights = WinRateView["auto_insights"];

type Metric = "pnl" | "win_rate";

const PNL_VIEW_IDS = [
  "by_month",
  "by_week",
  "by_day_of_week",
  "by_hour_of_day",
  "by_symbol",
  "by_strategy",
  "by_position",
  "by_duration_range",
  "drawdown_vs_date",
  "by_planned_risk_bucket",
] as const;
type PnlViewId = (typeof PNL_VIEW_IDS)[number];

const PNL_VIEW_LABELS: Record<PnlViewId, string> = {
  by_month: "By Month",
  by_week: "By Week",
  by_day_of_week: "By Day of Week",
  by_hour_of_day: "By Hour of Day",
  by_symbol: "By Symbol",
  by_strategy: "By Strategy",
  by_position: "By Position",
  by_duration_range: "By Duration",
  drawdown_vs_date: "Drawdown vs Date",
  by_planned_risk_bucket: "By Planned Risk",
};

const WIN_VIEW_IDS = [
  "by_price_range",
  "by_volume_range",
  "by_time_of_day",
  "by_day_of_week",
  "by_month",
  "by_year",
] as const;
type WinViewId = (typeof WIN_VIEW_IDS)[number];

const WIN_VIEW_LABELS: Record<WinViewId, string> = {
  by_price_range: "Price Range",
  by_volume_range: "Volume Range",
  by_time_of_day: "Time of Day",
  by_day_of_week: "Day of Week",
  by_month: "Month",
  by_year: "Year",
};

type Nullable = string | null | undefined;

function safeFormatMoney(amount: Nullable, currency: Nullable): string {
  if (amount === null || amount === undefined) return "—";
  if (currency === null || currency === undefined) return "—";
  try {
    return formatMoney(amount, { currency });
  } catch {
    return `${amount} ${currency}`;
  }
}

function formatPercent(decimal: Nullable, options: { decimals?: number } = {}): string {
  if (decimal === null || decimal === undefined) return "—";
  const n = Number(decimal);
  if (Number.isNaN(n)) return decimal;
  const decimals = options.decimals ?? 1;
  return `${(n * 100).toFixed(decimals)}%`;
}

function formatInteger(n: number): string {
  return n.toLocaleString("en-US");
}

// -- P&L rendering ----------------------------------------------------------

interface PnlChartProps {
  buckets: readonly ProfitLossBucket[];
  currency: string;
  testId: string;
}

/**
 * Simple SVG-free horizontal bar chart for the per-bucket P&L view. Each bucket
 * renders as a row -- a green right-aligned bar for positive total_pnl, a rose
 * left-aligned bar for negative total_pnl. Width scales to the largest absolute
 * P&L across all buckets. Zero-padded buckets render with no bar.
 */
function PnlBarChart({ buckets, currency, testId }: PnlChartProps) {
  if (buckets.length === 0) {
    return (
      <div className="text-sm text-on-surface-variant" data-testid={`${testId}-empty`}>
        No data in the selected range.
      </div>
    );
  }

  const maxAbs = buckets.reduce((acc, b) => {
    const a = Math.abs(Number(b.total_pnl_amount));
    return Number.isFinite(a) && a > acc ? a : acc;
  }, 0);

  return (
    <div className="space-y-1" data-testid={testId}>
      {buckets.map((bucket) => {
        const amount = Number(bucket.total_pnl_amount);
        const isPositive = amount >= 0;
        const widthPct = maxAbs > 0 ? (Math.abs(amount) / maxAbs) * 50 : 0;
        return (
          <div
            key={bucket.label}
            className="grid grid-cols-[8rem_1fr_8rem] items-center gap-2 text-sm"
            data-testid={`${testId}-row-${bucket.label}`}
          >
            <div className="truncate font-medium text-on-surface" title={bucket.label}>
              {bucket.label}
            </div>
            <div className="relative h-4 bg-surface-container-high">
              <div className="absolute inset-y-0 left-1/2 w-px bg-outline" />
              {amount !== 0 && (
                <div
                  className={cn("absolute inset-y-0", isPositive ? "bg-success" : "bg-danger")}
                  style={
                    isPositive
                      ? { left: "50%", width: `${widthPct}%` }
                      : { right: "50%", width: `${widthPct}%` }
                  }
                />
              )}
            </div>
            <div
              className={cn(
                "text-right tabular-nums",
                isPositive ? "text-success" : "text-danger",
              )}
            >
              {safeFormatMoney(bucket.total_pnl_amount, currency)}
              <span className="ml-2 text-xs text-on-surface-variant">
                {formatInteger(bucket.trade_count)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PnlInsightsPanel({
  insights,
  currency,
  testId,
}: {
  insights: ProfitLossInsights;
  currency: string;
  testId: string;
}) {
  const bestText =
    insights.best === null || insights.best === undefined
      ? "—"
      : `${insights.best.label}: ${safeFormatMoney(insights.best.total_pnl_amount, currency)}`;
  const worstText =
    insights.worst === null || insights.worst === undefined
      ? "—"
      : `${insights.worst.label}: ${safeFormatMoney(insights.worst.total_pnl_amount, currency)}`;
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle>Auto-Insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">Best</span>
          <span className="font-medium text-success" data-testid={`${testId}-best`}>
            {bestText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">Worst</span>
          <span className="font-medium text-danger" data-testid={`${testId}-worst`}>
            {worstText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">
            Top-bucket concentration
          </span>
          <span data-testid={`${testId}-concentration`}>
            {formatPercent(insights.concentration_top_pct, { decimals: 1 })}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">
            Best vs worst spread
          </span>
          <span data-testid={`${testId}-divergence`}>
            {formatPercent(insights.divergence_pct, { decimals: 1 })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function selectPnlView(data: ProfitLossChartsResponse, viewId: PnlViewId): ProfitLossView {
  switch (viewId) {
    case "by_month":
      return data.by_month;
    case "by_week":
      return data.by_week;
    case "by_day_of_week":
      return data.by_day_of_week;
    case "by_hour_of_day":
      return data.by_hour_of_day;
    case "by_symbol":
      return data.by_symbol;
    case "by_strategy":
      return data.by_strategy;
    case "by_position":
      return data.by_position;
    case "by_duration_range":
      return data.by_duration_range;
    case "drawdown_vs_date":
      return data.drawdown_vs_date;
    case "by_planned_risk_bucket":
      return data.by_planned_risk_bucket;
  }
}

function PnlPanel({ data, viewId }: { data: ProfitLossChartsResponse; viewId: PnlViewId }) {
  const view = selectPnlView(data, viewId);
  return (
    <div className="grid gap-4 lg:grid-cols-[2fr_1fr]" data-testid={`pnl-charts-panel-${viewId}`}>
      <Card>
        <CardHeader>
          <CardTitle>{PNL_VIEW_LABELS[viewId]}</CardTitle>
        </CardHeader>
        <CardContent>
          <PnlBarChart
            buckets={view.buckets}
            currency={data.currency}
            testId={`pnl-charts-bars-${viewId}`}
          />
        </CardContent>
      </Card>
      <PnlInsightsPanel
        insights={view.auto_insights}
        currency={data.currency}
        testId={`pnl-charts-insights-${viewId}`}
      />
    </div>
  );
}

// -- Win-rate rendering -----------------------------------------------------

interface WinChartProps {
  buckets: readonly WinRateBucket[];
  testId: string;
}

/**
 * Per-bucket wins (emerald) + scratches (slate) + losses (rose) stacked row.
 * The bucket's win rate renders to the right; empty zero-padded buckets render
 * a neutral em-dash.
 */
function WinRateBarChart({ buckets, testId }: WinChartProps) {
  if (buckets.length === 0) {
    return (
      <div className="text-sm text-on-surface-variant" data-testid={`${testId}-empty`}>
        No data in the selected range.
      </div>
    );
  }

  const maxCount = buckets.reduce((acc, b) => (b.trade_count > acc ? b.trade_count : acc), 0);

  return (
    <div className="space-y-1" data-testid={testId}>
      {buckets.map((bucket) => {
        const widthPct = maxCount > 0 ? (bucket.trade_count / maxCount) * 100 : 0;
        const winsPct = bucket.trade_count > 0 ? (bucket.wins / bucket.trade_count) * widthPct : 0;
        const lossesPct =
          bucket.trade_count > 0 ? (bucket.losses / bucket.trade_count) * widthPct : 0;
        const scratchesPct =
          bucket.trade_count > 0 ? (bucket.scratches / bucket.trade_count) * widthPct : 0;
        return (
          <div
            key={bucket.label}
            className="grid grid-cols-[8rem_1fr_8rem] items-center gap-2 text-sm"
            data-testid={`${testId}-row-${bucket.label}`}
          >
            <div className="truncate font-medium text-on-surface" title={bucket.label}>
              {bucket.label}
            </div>
            <div className="relative h-4 bg-surface-container-high">
              {winsPct > 0 && (
                <div
                  className="absolute inset-y-0 bg-success"
                  style={{ left: 0, width: `${winsPct}%` }}
                />
              )}
              {scratchesPct > 0 && (
                <div
                  className="absolute inset-y-0 bg-on-surface-muted"
                  style={{ left: `${winsPct}%`, width: `${scratchesPct}%` }}
                />
              )}
              {lossesPct > 0 && (
                <div
                  className="absolute inset-y-0 bg-danger"
                  style={{ left: `${winsPct + scratchesPct}%`, width: `${lossesPct}%` }}
                />
              )}
            </div>
            <div className="text-right tabular-nums text-on-surface">
              {formatPercent(bucket.win_rate, { decimals: 1 })}
              <span className="ml-2 text-xs text-on-surface-variant">
                {formatInteger(bucket.trade_count)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WinRateInsightsPanel({
  insights,
  currency,
  testId,
}: {
  insights: WinRateInsights;
  currency: string;
  testId: string;
}) {
  const bestText =
    insights.best === null || insights.best === undefined
      ? "—"
      : `${insights.best.label}: ${safeFormatMoney(insights.best.total_pnl_amount, currency)}`;
  const worstText =
    insights.worst === null || insights.worst === undefined
      ? "—"
      : `${insights.worst.label}: ${safeFormatMoney(insights.worst.total_pnl_amount, currency)}`;
  const bestWinrateText =
    insights.best_winrate === null || insights.best_winrate === undefined
      ? "—"
      : `${insights.best_winrate.label}: ${formatPercent(insights.best_winrate.win_rate, { decimals: 1 })}`;
  const worstWinrateText =
    insights.worst_winrate === null || insights.worst_winrate === undefined
      ? "—"
      : `${insights.worst_winrate.label}: ${formatPercent(insights.worst_winrate.win_rate, { decimals: 1 })}`;
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle>Auto-Insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">
            Best win rate
          </span>
          <span className="font-medium text-success" data-testid={`${testId}-best-winrate`}>
            {bestWinrateText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">
            Worst win rate
          </span>
          <span className="font-medium text-danger" data-testid={`${testId}-worst-winrate`}>
            {worstWinrateText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">
            Win-rate spread
          </span>
          <span data-testid={`${testId}-winrate-divergence`}>
            {formatPercent(insights.winrate_divergence_pct, { decimals: 1 })}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">
            Best by P&amp;L
          </span>
          <span className="font-medium text-success" data-testid={`${testId}-best`}>
            {bestText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">
            Worst by P&amp;L
          </span>
          <span className="font-medium text-danger" data-testid={`${testId}-worst`}>
            {worstText}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function selectWinView(data: WinRateChartsResponse, viewId: WinViewId): WinRateView {
  switch (viewId) {
    case "by_price_range":
      return data.by_price_range;
    case "by_volume_range":
      return data.by_volume_range;
    case "by_time_of_day":
      return data.by_time_of_day;
    case "by_day_of_week":
      return data.by_day_of_week;
    case "by_month":
      return data.by_month;
    case "by_year":
      return data.by_year;
  }
}

function WinPanel({ data, viewId }: { data: WinRateChartsResponse; viewId: WinViewId }) {
  const view = selectWinView(data, viewId);
  return (
    <div
      className="grid gap-4 lg:grid-cols-[2fr_1fr]"
      data-testid={`win-rate-charts-panel-${viewId}`}
    >
      <Card>
        <CardHeader>
          <CardTitle>{WIN_VIEW_LABELS[viewId]}</CardTitle>
        </CardHeader>
        <CardContent>
          <WinRateBarChart buckets={view.buckets} testId={`win-rate-charts-bars-${viewId}`} />
        </CardContent>
      </Card>
      <WinRateInsightsPanel
        insights={view.auto_insights}
        currency={data.currency}
        testId={`win-rate-charts-insights-${viewId}`}
      />
    </div>
  );
}

// -- Shared tab strip -------------------------------------------------------

function ViewTabs<Id extends string>({
  ids,
  labels,
  active,
  onSelect,
  ariaLabel,
}: {
  ids: readonly Id[];
  labels: Record<Id, string>;
  active: Id;
  onSelect: (id: Id) => void;
  ariaLabel: string;
}) {
  return (
    <nav
      className="flex flex-wrap gap-2 border-b border-outline-variant"
      role="tablist"
      aria-label={ariaLabel}
    >
      {ids.map((id) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={active === id}
          data-testid={`view-tab-${id}`}
          onClick={() => onSelect(id)}
          className={cn(
            "rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition-colors",
            active === id
              ? "border-primary text-on-surface"
              : "border-transparent text-on-surface-variant hover:text-on-surface",
          )}
        >
          {labels[id]}
        </button>
      ))}
    </nav>
  );
}

const METRIC_BUTTON_BASE =
  "rounded-control px-3 py-1.5 text-sm font-medium transition-colors";
const METRIC_BUTTON_ACTIVE = "bg-surface-container-high text-on-surface";
const METRIC_BUTTON_INACTIVE =
  "text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface";

export function ProfitLossChartsPage() {
  // Date/account/tag scope comes from the shared Analytics FilterBar; this page
  // reads the same URL search. The metric toggle swaps the whole view and only
  // the active metric's query runs.
  const filters = useGlobalFilters();
  const [metric, setMetric] = useState<Metric>("pnl");
  const [pnlView, setPnlView] = useState<PnlViewId>("by_month");
  const [winView, setWinView] = useState<WinViewId>("by_price_range");

  const pnlQuery = useProfitLossCharts(filters, { enabled: metric === "pnl" });
  const winQuery = useWinRateCharts(filters, { enabled: metric === "win_rate" });
  const activeQuery = metric === "pnl" ? pnlQuery : winQuery;

  return (
    <main className="space-y-6 p-6">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            Charts
          </h1>
          <p className="text-sm text-on-surface-variant">
            Per-bucket P&amp;L and win-rate views with deterministic Auto-Insights side panels.
          </p>
        </div>
        <div
          className="inline-flex shrink-0 items-center gap-1 rounded-control bg-surface-container p-1"
          role="group"
          aria-label="Chart metric"
        >
          <button
            type="button"
            data-testid="metric-toggle-pnl"
            aria-pressed={metric === "pnl"}
            onClick={() => setMetric("pnl")}
            className={cn(
              METRIC_BUTTON_BASE,
              metric === "pnl" ? METRIC_BUTTON_ACTIVE : METRIC_BUTTON_INACTIVE,
            )}
          >
            P&amp;L
          </button>
          <button
            type="button"
            data-testid="metric-toggle-win_rate"
            aria-pressed={metric === "win_rate"}
            onClick={() => setMetric("win_rate")}
            className={cn(
              METRIC_BUTTON_BASE,
              metric === "win_rate" ? METRIC_BUTTON_ACTIVE : METRIC_BUTTON_INACTIVE,
            )}
          >
            Win rate
          </button>
        </div>
      </header>

      {metric === "pnl" ? (
        <ViewTabs
          ids={PNL_VIEW_IDS}
          labels={PNL_VIEW_LABELS}
          active={pnlView}
          onSelect={setPnlView}
          ariaLabel="Profit & Loss chart views"
        />
      ) : (
        <ViewTabs
          ids={WIN_VIEW_IDS}
          labels={WIN_VIEW_LABELS}
          active={winView}
          onSelect={setWinView}
          ariaLabel="Win-rate chart views"
        />
      )}

      {activeQuery.isLoading && (
        <div className="text-sm text-on-surface-variant" data-testid="charts-loading">
          Loading charts…
        </div>
      )}
      {activeQuery.error !== null && activeQuery.error !== undefined && (
        <div className="text-sm text-error" data-testid="charts-error">
          Failed to load charts. Try changing the filter and reloading.
        </div>
      )}

      {metric === "pnl" && pnlQuery.data !== undefined && (
        <section
          role="tabpanel"
          aria-labelledby={`view-tab-${pnlView}`}
          data-testid={`view-panel-${pnlView}`}
        >
          <PnlPanel data={pnlQuery.data} viewId={pnlView} />
        </section>
      )}
      {metric === "win_rate" && winQuery.data !== undefined && (
        <section
          role="tabpanel"
          aria-labelledby={`view-tab-${winView}`}
          data-testid={`view-panel-${winView}`}
        >
          <WinPanel data={winQuery.data} viewId={winView} />
        </section>
      )}
    </main>
  );
}
